import React from "react";
import cn from "classnames";
import PropTypes from "prop-types";
import Link from "next/link";

import { formatNumber } from "util/strings";
import ListingCard from "components/cards/ListingCard";

import ListingPrice from "../Domain/ListingPrice";

import styles from "./style.module.scss";
import BaseMarker from "./BaseMarker";

const DefaultExpandedMarker = ({ listing, showAccuratePrices }) => (
  <Link
    passHref
    href={`/building${listing.building.slug}/${listing.building.identity}?listingId=${listing.id}`}
  >
    <ListingCard
      listing={listing}
      className={styles.mapCard}
      showAccuratePrices={showAccuratePrices}
    />
  </Link>
);

DefaultExpandedMarker.propTypes = {
  listing: PropTypes.shape({
    id: PropTypes.string,
    squareFeet: PropTypes.number,
    accuratePsf: PropTypes.string,
    building: PropTypes.shape({
      id: PropTypes.string,
      identity: PropTypes.string,
      lat: PropTypes.number,
      lng: PropTypes.number,
      slug: PropTypes.string,
    }),
  }).isRequired,
  showAccuratePrices: PropTypes.bool,
};

DefaultExpandedMarker.defaultProps = {
  showAccuratePrices: false,
};
export default function ListingMarker(props) {
  const {
    style,
    render,
    listing,
    showStar,
    isHovered,
    className,
    isExpanded,
    hoverClass,
    hoverListing,
    selectListing,
    allowsExpanded,
    showAccuratePrices,
    renderExpandedMarker,
    latitude,
    longitude,
  } = props;

  const onClick = (evt) => {
    evt.stopPropagation();
    selectListing(listing.id);
  };

  const onMouseEnter = () => hoverListing(listing);

  const onMouseLeave = () => hoverListing();

  const renderDefaultMarker = () => (
    <div data-jest="defaultMarker">
      {allowsExpanded && isExpanded && (
        <>{renderExpandedMarker(listing, showAccuratePrices, styles.mapCard)}</>
      )}
      {!isExpanded && showStar && (
        <span className={`icon icon-star-filled ${styles.star}`} />
      )}
      {!isExpanded && (
        <span>
          {" "}
          {formatNumber(listing.squareFeet)} <small>sqft</small>{" "}
        </span>
      )}
      {isHovered && !isExpanded && listing.accuratePsf && (
        <span className={styles.markerHover}>
          <ListingPrice
            listing={listing}
            showAccurate={showAccuratePrices}
            estimatedPriceDisplayStyle="est"
          />
          {showAccuratePrices && !listing.accuratePsf && (
            <span>
              {" "}
              ? <small>Price</small>{" "}
            </span>
          )}
        </span>
      )}
    </div>
  );

  return (
    <BaseMarker
      id={listing.building.id}
      key={`marker-${listing.id}`}
      latitude={latitude}
      longitude={longitude}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(styles[className], {
        [styles[hoverClass]]: isHovered,
        [styles.expanded]: isExpanded,
      })}
      style={style}
    >
      <div
        key={`inner-marker-${listing.building.id}-${isExpanded}-${isHovered}`}
        className={styles.markerInner}
        style={{ pointerEvents: "none" }}
      >
        {render
          ? render({ building: listing.building })
          : renderDefaultMarker()}
      </div>
    </BaseMarker>
  );
}

ListingMarker.defaultProps = {
  style: null,
  render: null,
  className: null,
  showStar: false,
  hoverClass: "hovered",
  allowsExpanded: true,
  hoverListing: () => {},
  showAccuratePrices: false,
  renderExpandedMarker: DefaultExpandedMarker,
};

ListingMarker.propTypes = {
  render: PropTypes.func,
  style: PropTypes.string,
  showStar: PropTypes.bool,
  className: PropTypes.string,
  hoverClass: PropTypes.string,
  hoverListing: PropTypes.func,
  allowsExpanded: PropTypes.bool,
  showAccuratePrices: PropTypes.bool,
  isHovered: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  selectListing: PropTypes.func.isRequired,
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  listing: PropTypes.shape({
    sublease: PropTypes.bool,
    leaseTerm: PropTypes.string,
    description: PropTypes.string,
    accuratePsf: PropTypes.number,
    estimatedPsf: PropTypes.number,
    id: PropTypes.string.isRequired,
    accurateMonthlyPrice: PropTypes.number,
    squareFeet: PropTypes.number.isRequired,
    estimatedMonthlyPrice: PropTypes.number,
    floorAndSuite: PropTypes.string.isRequired,
    building: PropTypes.shape({
      id: PropTypes.string.isRequired,
      identity: PropTypes.string,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      zip: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      address: PropTypes.string.isRequired,
      classification: PropTypes.string,
    }),
  }).isRequired,
  renderExpandedMarker: PropTypes.func,
};

export { ListingMarker };
